/**
 * The one pool API client.
 *
 * Both the kernel (website config fetch at init) and the pool module
 * (post/content fetch) go through this transport, so base-URL resolution and
 * envelope handling exist exactly once:
 *
 *   GET  /web/website/public/{domain}
 *   POST /post/public/id/{locale}
 */

import { joinUrl } from '../../util/url';
import { normalizePoolDomain } from './domain';
import { DEFAULT_API_BASE_URL } from './constants';
import type { PoolWebsitePayload } from './types';
import { debug } from '../../util/log';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Yoto-pool-rs returns either `{ success: true, result: T }` or `{ result: T }`
 * (older responses). Both shapes return the unwrapped payload here.
 */
export function unwrapPoolEnvelope(body: unknown): unknown {
  if (!isObject(body)) {
    throw new Error('pool response: not an object');
  }
  if (body.success === false) {
    const errors = Array.isArray(body.errors) ? body.errors.join(', ') : 'unknown';
    throw new Error(`pool response: success=false (${errors})`);
  }
  if ('result' in body) {
    const result = (body as { result: unknown }).result;
    if (result === undefined || result === null) {
      throw new Error('pool response: missing result');
    }
    return result;
  }
  // Some endpoints return the payload directly; treat the whole body as result.
  return body;
}

/** Lowercases the pool locale route segment. */
export function normalizePoolLocale(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error('fetchPost: locale required');
  }
  return trimmed.toLowerCase();
}

/* -------------------------------------------------------------------------- */
/* Website                                                                     */
/* -------------------------------------------------------------------------- */

export interface FetchWebsitePayloadOptions {
  domain: string;
  apiBaseUrl?: string;
  signal?: AbortSignal;
  /**
   * ISO 3166-1 alpha-2 country override (`?cc=` on the pool request).
   * Pool applies this ahead of edge geo headers (CMP/geo in the response).
   */
  country?: string | null;
}

/** Fetches the raw (unwrapped, un-normalized) public website payload. */
export async function fetchWebsitePayload(
  opts: FetchWebsitePayloadOptions,
): Promise<PoolWebsitePayload> {
  const url = buildWebsiteUrl(opts.apiBaseUrl ?? DEFAULT_API_BASE_URL, opts.domain, {
    country: opts.country,
  });
  debug('fetching website config', { url, domain: opts.domain, country: opts.country || undefined });
  const body = await poolFetchJson(url, buildInit('GET', undefined, opts.signal));
  const result = unwrapPoolEnvelope(body);
  if (!isObject(result)) {
    throw new Error('pool response: result is not an object');
  }
  debug('website config fetched', {
    domain: (result as { website?: { domain?: string } }).website?.domain,
    adAccounts: Array.isArray((result as { ad_accounts?: unknown }).ad_accounts)
      ? (result as { ad_accounts: unknown[] }).ad_accounts.length
      : 0,
  });
  return result as unknown as PoolWebsitePayload;
}

/**
 * Compose the request URL while preserving any query string on `apiBaseUrl`.
 * Useful when consumers append query params to the pool base URL (e.g. testing
 * overrides) without baking them into the SDK API.
 *
 * Optional `country` becomes `?cc=` (pool geo/CMP override; beats edge headers).
 */
export function buildWebsiteUrl(
  apiBaseUrl: string,
  domain: string,
  opts?: { country?: string | null },
): string {
  const normalized = normalizePoolDomain(domain);
  if (!normalized) throw new Error('config: domain required');
  const url = new URL(apiBaseUrl, 'http://_relative_/');
  const basePath = url.pathname.replace(/\/+$/, '');
  url.pathname = `${basePath}/web/website/public/${encodeURIComponent(normalized)}`;
  const country = normalizePoolCountry(opts?.country);
  if (country) url.searchParams.set('cc', country);
  // Strip the synthetic origin if `apiBaseUrl` was a relative URL.
  return url.origin === 'http://_relative_' ? url.pathname + url.search : url.toString();
}

function normalizePoolCountry(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const country = String(raw).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(country) ? country : null;
}

/* -------------------------------------------------------------------------- */
/* Post                                                                        */
/* -------------------------------------------------------------------------- */

export interface FetchPostPayloadOptions {
  /** 2-letter language code (case-insensitive). `en-us` is accepted and normalized to `en`. */
  locale: string;
  id: string | number;
  domain: string;
  /**
   * Preview/private-mode parity with yoto-web: sends `include_drafts=1` and
   * `pool=1` in the post request body.
   */
  includeDrafts?: boolean;
  apiBaseUrl?: string;
  signal?: AbortSignal;
}

/** Fetches the raw (unwrapped, un-normalized) public post payload. */
export async function fetchPostPayload(opts: FetchPostPayloadOptions): Promise<unknown> {
  const locale = normalizePoolLocale(opts.locale);
  const url = buildPostUrl(opts.apiBaseUrl ?? DEFAULT_API_BASE_URL, locale);
  const domain = normalizePoolDomain(opts.domain);
  if (!domain) throw new Error('fetchPost: domain required');
  const requestBody: Record<string, unknown> = { id: opts.id, domain };
  if (opts.includeDrafts) {
    requestBody.include_drafts = 1;
    requestBody.pool = 1;
  }
  const body = await poolFetchJson(url, buildInit('POST', JSON.stringify(requestBody), opts.signal));
  return unwrapPoolEnvelope(body);
}

export function buildPostUrl(baseUrl: string, locale: string): string {
  if (!locale) throw new Error('fetchPost: locale required');
  return joinUrl(baseUrl, `/post/public/id/${encodeURIComponent(locale)}`);
}

/* -------------------------------------------------------------------------- */
/* Shared transport                                                            */
/* -------------------------------------------------------------------------- */

function buildInit(
  method: 'GET' | 'POST',
  body: string | undefined,
  signal: AbortSignal | undefined,
): RequestInit {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const init: RequestInit = { method, credentials: 'omit', headers };
  if (body !== undefined) init.body = body;
  if (signal) init.signal = signal;
  return init;
}

async function poolFetchJson(url: string, init: RequestInit): Promise<unknown> {
  const res = await fetch(url, init);
  if (!res.ok) {
    throw new Error(`pool fetch failed: ${res.status}`);
  }
  return (await res.json()) as unknown;
}
